import type { AppLoadContext, LoaderFunctionArgs } from "react-router";

import { hrInfoEmployeeClient } from "~/features/integration/hr-api/employee-client.server";
import { populateDepartureAggregateWithEmployees } from "~/features/integration/hr-api/populate-staff-employee";
import { topasAggregatesClient } from "~/features/integration/topas-aggregates/client.server";
import { commonLogger } from "~/features/logging/logging.server";
import { departureQuery } from "~/features/query-cache/queries";
import { type SelectedTrain } from "~/features/train-info/schemas";
import { formatApiDate } from "~/utils/date";
import { fetchJson } from "~/utils/fetch-json";
import { fetchFromKotlinBFFIfAllowed } from "~/utils/kotlin-bff.server";
import { badRequest } from "~/utils/response.server";

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import type { Employee } from "@vy-clients/hr-info-api";
import { DepartureAggregateFromJSON } from "@vy-clients/topas-aggregates";
import { formatISO } from "date-fns";
import type { Jsonify } from "type-fest";

const fetchFromTopasAggregates =
  (context: AppLoadContext, trainId: string, date: string) => async () => {
    const hrInfoClient = hrInfoEmployeeClient(context.session?.user);
    const client = topasAggregatesClient(context);

    const result = await client.getDepartureAggregate({
      // @ts-expect-error - Type expects Date, but we're sending a string from URL params
      date,
      trainId,
    });

    const staff = result?.staffOnboardAggregate || [];
    const employeeNumbers = new Set<string>(
      staff.map(({ employeeNumber }) => employeeNumber),
    );
    const employees = await hrInfoClient.getEmployees([...employeeNumbers]);
    const employeesByEmployeeNumber: Record<string, Employee> =
      employees.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.employeeNumber]: curr,
        }),
        {},
      );

    return (
      result &&
      populateDepartureAggregateWithEmployees(result, employeesByEmployeeNumber)
    );
  };

export async function loader({ context, request }: LoaderFunctionArgs) {
  const searchParams = new URL(request.url).searchParams;
  const date = searchParams.get(DATE_PARAM);
  const trainId = searchParams.get(TRAIN_ID_PARAM);

  if (!date) {
    commonLogger.error("departure_no_date", {
      params: Object.fromEntries(searchParams),
    });
    throw badRequest("queryParam date was empty");
  }

  if (trainId === null) {
    commonLogger.error("departure_no_train_id", {
      params: Object.fromEntries(searchParams),
    });
    throw badRequest("queryParam trainId was empty");
  }

  return fetchFromKotlinBFFIfAllowed({
    context,

    path: `/v0/train-info/departures/${trainId}/${formatApiDate(new Date(date))}`,
    fallbackFetcher: fetchFromTopasAggregates(context, trainId, date),
    kotlinBffDataValidator: DepartureAggregateFromJSON,
  });
}

export const DATE_PARAM = "date";
export const TRAIN_ID_PARAM = "trainId";

export type DepartureData = Jsonify<
  Awaited<ReturnType<ReturnType<typeof fetchFromTopasAggregates>>>
>;

export const departureQueryFn = (params?: URLSearchParams) => {
  return () =>
    fetchJson<DepartureData>(`/api/departure`, {
      params,
    });
};
export const useDeparture = (
  selectedTrain: SelectedTrain,
  options?: Omit<UseQueryOptions<DepartureData>, "queryKey" | "queryFn">,
) => {
  return useQuery({
    enabled: !!selectedTrain,
    queryFn: departureQueryFn(
      new URLSearchParams(
        selectedTrain?.nominalDate
          ? {
              [DATE_PARAM]: formatISO(selectedTrain.nominalDate, {
                representation: "date",
              }),
              [TRAIN_ID_PARAM]: selectedTrain.trainId,
            }
          : {},
      ),
    ),
    ...departureQuery(selectedTrain),
    ...options,
  });
};
